import React from 'react'
import { withTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import * as UIcons from '@iconscout/react-unicons'
import BreadCrumb from 'components/BreadCrumb'
import Btn from 'components/Btn'
import StatusPill from '../../shared/StatusPill'
import KeyValueList from '../../shared/KeyValueList'
import ActionsMenu from '../../shared/ActionsMenu'

function Card({ title, action, children }) {
  return (
    <div className="w-full bg-white rounded-2xl border border-border-lines overflow-hidden mb-5">
      <div className="flex items-center justify-between px-6 py-3 bg-background">
        <p className="text-xs font-semibold uppercase tracking-wide text-secondary">{title}</p>
        {action}
      </div>

      <div className="p-6">
        {children}
      </div>
    </div>
  )
}

export default withTranslation()(function UserDetail({ t, user, branchName, onEdit, onAssignRole, onResetPassword, onToggleStatus }) {
  const isActive = (user.status ?? 'active') !== 'inactive' && user.status !== 'suspended'
  const role = user.roles?.[0]

  const menuActions = [
    { key: 'edit', icon: UIcons.UilEditAlt, label: t('settings.user_management.edit_user'), onClick: onEdit },
    { key: 'assign-role', icon: UIcons.UilUserPlus, label: t('settings.user_management.assign_role'), onClick: onAssignRole },
    { key: 'reset-password', icon: UIcons.UilLock, label: t('settings.user_management.reset_password'), onClick: onResetPassword },
    {
      key: 'toggle-status',
      icon: UIcons.UilBan,
      label: t(isActive ? 'settings.user_management.deactivate' : 'settings.user_management.enable'),
      danger: isActive,
      onClick: onToggleStatus
    }
  ]

  return (
    <section className="w-full max-w-4xl mx-auto">
      <div className="mb-5">
        <BreadCrumb
          items={[
            { name: t('settings.hub.title'), url: '/settings' },
            { name: t('settings.hub.team.title'), url: '/settings/team-access' },
            { name: t('settings.user_management.page_title'), url: '/settings/user-management' },
            { name: t('settings.user_management.detail.user_detail') }
          ]}
        />
      </div>

      <Card
        title={t('settings.user_management.detail.user_detail')}
        action={
          <div className="flex items-center gap-2">
            <Btn
              content={() => <span className="flex items-center gap-1.5"><UIcons.UilEditAlt size={14} /> {t('misc.edit')}</span>}
              className="text-dark-blue bg-white border border-border-lines rounded-full h-9 px-4 font-medium"
              click={onEdit}
            />

            <ActionsMenu items={menuActions} />
          </div>
        }
      >
        <div className="flex items-start gap-4 mb-6">
          {
            user.avatar ? (
              <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-icon-bg flex items-center justify-center">
                <UIcons.UilUser className="text-primary" size={28} />
              </div>
            )
          }

          <div>
            <h1 className="text-xl font-bold text-dark-blue">{`${user.first_name ?? ''} ${user.last_name ?? ''}`}</h1>
            <p className="text-sm text-secondary">{user.email}</p>
          </div>
        </div>

        <KeyValueList
          items={[
            { label: t('settings.user_management.detail.status'), value: <StatusPill active={isActive} /> },
            { label: t('settings.user_management.detail.role'), value: role?.display_name },
            { label: t('settings.user_management.detail.assigned_branch'), value: branchName },
            {
              label: t('settings.user_management.detail.last_login'),
              value: user.last_login_at ? dayjs(user.last_login_at).format('MMM D, YYYY. h:mm A') : null
            }
          ]}
        />
      </Card>

      <Card title={t('settings.user_management.detail.personal_information')}>
        <KeyValueList
          items={[
            { label: t('settings.user_management.detail.full_name'), value: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() },
            { label: t('settings.user_management.detail.email'), value: user.email },
            { label: t('settings.user_management.detail.phone_number'), value: user.phone },
            { label: t('settings.user_management.detail.employee_id'), value: user.employee_id },
            { label: t('settings.user_management.detail.gender'), value: user.gender },
            {
              label: t('settings.user_management.detail.account_created'),
              value: user.created_at ? dayjs(user.created_at).format('MMM D, YYYY. h:mm A') : null
            }
          ]}
        />
      </Card>

      <Card title={t('settings.user_management.detail.role_access')}>
        <KeyValueList
          items={[
            { label: t('settings.user_management.detail.current_role'), value: role?.display_name },
            { label: t('settings.user_management.detail.assigned_permissions'), value: role?.permissions?.length },
            { label: t('settings.user_management.detail.branch'), value: branchName }
          ]}
        />
      </Card>
    </section>
  )
})
